import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ArrowLeft, Save, X } from 'lucide-react'
import { Button, Input, Select, Card, CardHeader, CardBody, CardFooter } from '../../components/ui'
import type { CampaignFormData } from '../../types'

const advertisers = [
  { value: 'adv-1', label: 'ゲーム会社A' },
  { value: 'adv-2', label: 'ゲーム会社B' },
  { value: 'adv-3', label: '小売会社C' },
  { value: 'adv-self', label: '自社' },
]

const bidTypes = [
  { value: 'cpm', label: 'CPM (1000インプレッション単価)' },
  { value: 'cpc', label: 'CPC (クリック単価)' },
  { value: 'cpa', label: 'CPA (コンバージョン単価)' },
]

const priorities = [
  { value: '0', label: '通常' },
  { value: '1', label: '高' },
  { value: '2', label: '最優先' },
]

const frequencyPeriods = [
  { value: 'hour', label: '時間' },
  { value: 'day', label: '日' },
  { value: 'week', label: '週' },
]

export default function CampaignCreatePage() {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<CampaignFormData>({
    defaultValues: {
      name: '',
      advertiserId: '',
      description: '',
      budget: undefined,
      dailyBudget: undefined,
      bidType: 'cpm',
      bidAmount: undefined,
      startDate: '',
      endDate: '',
      priority: 0,
      frequencyCap: undefined,
      frequencyCapPeriod: 'day',
    },
  })

  const watchBidType = watch('bidType')

  const onSubmit = async (data: CampaignFormData) => {
    setIsSubmitting(true)
    try {
      console.log('Create campaign:', data)
      // API call would go here
      await new Promise(resolve => setTimeout(resolve, 1000))
      navigate('/campaigns')
    } catch (error) {
      console.error('Failed to create campaign:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/campaigns" className="breadcrumb-item flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" />
          キャンペーン一覧
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="text-gray-900">新規作成</span>
      </div>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">新規キャンペーン作成</h1>
        <p className="text-sm text-gray-500 mt-1">広告キャンペーンの基本情報を入力してください</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Basic Info */}
        <Card className="mb-6">
          <CardHeader>
            <h3 className="font-semibold text-gray-900">基本情報</h3>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="キャンペーン名"
                placeholder="キャンペーン名を入力"
                required
                error={errors.name?.message}
                {...register('name', { required: 'キャンペーン名は必須です' })}
              />
              <Select
                label="広告主"
                options={advertisers}
                placeholder="広告主を選択"
                required
                error={errors.advertiserId?.message}
                {...register('advertiserId', { required: '広告主は必須です' })}
              />
            </div>

            <div>
              <label className="form-label">説明</label>
              <textarea
                className="form-textarea"
                rows={3}
                placeholder="キャンペーンの説明を入力（任意）"
                {...register('description')}
              />
            </div>
          </CardBody>
        </Card>

        {/* Schedule */}
        <Card className="mb-6">
          <CardHeader>
            <h3 className="font-semibold text-gray-900">配信期間</h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="datetime-local"
                label="開始日時"
                required
                error={errors.startDate?.message}
                {...register('startDate', { required: '開始日時は必須です' })}
              />
              <Input
                type="datetime-local"
                label="終了日時"
                required
                error={errors.endDate?.message}
                {...register('endDate', { required: '終了日時は必須です' })}
              />
            </div>
          </CardBody>
        </Card>

        {/* Budget */}
        <Card className="mb-6">
          <CardHeader>
            <h3 className="font-semibold text-gray-900">予算・入札設定</h3>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                type="number"
                label="総予算"
                placeholder="1000000"
                hint="キャンペーン全体の予算上限"
                leftIcon={<span className="text-gray-400">¥</span>}
                {...register('budget', { valueAsNumber: true })}
              />
              <Input
                type="number"
                label="日予算"
                placeholder="50000"
                hint="1日あたりの予算上限"
                leftIcon={<span className="text-gray-400">¥</span>}
                {...register('dailyBudget', { valueAsNumber: true })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="入札方式"
                options={bidTypes}
                {...register('bidType')}
              />
              <Input
                type="number"
                label={`入札額 (${watchBidType?.toUpperCase() || 'CPM'})`}
                placeholder="500"
                leftIcon={<span className="text-gray-400">¥</span>}
                {...register('bidAmount', { valueAsNumber: true })}
              />
            </div>
          </CardBody>
        </Card>

        {/* Advanced Settings */}
        <Card className="mb-6">
          <CardHeader>
            <h3 className="font-semibold text-gray-900">詳細設定</h3>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Select
                label="優先度"
                options={priorities}
                {...register('priority', { valueAsNumber: true })}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  type="number"
                  label="フリークエンシーキャップ"
                  placeholder="3"
                  hint="表示回数制限"
                  {...register('frequencyCap', { valueAsNumber: true })}
                />
                <Select
                  label="期間"
                  options={frequencyPeriods}
                  {...register('frequencyCapPeriod')}
                />
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Actions */}
        <Card>
          <CardFooter className="flex justify-end gap-3">
            <Button 
              type="button" 
              variant="secondary"
              leftIcon={<X className="w-4 h-4" />}
              onClick={() => navigate('/campaigns')}
            >
              キャンセル
            </Button>
            <Button 
              type="submit" 
              isLoading={isSubmitting}
              leftIcon={<Save className="w-4 h-4" />}
            >
              保存して作成
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  )
}

